import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProduct } from '../api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import { ChevronRight, Package, Calendar, Tag } from 'lucide-react';

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');

  useEffect(() => {
    setLoading(true);
    getProduct(id)
      .then(setProduct)
      .finally(() => setLoading(false));
  }, [id]);

  const formatPrice = (price) =>
    price?.toLocaleString('en-CA', { minimumFractionDigits: 2, style: 'currency', currency: 'CAD' });

  const conditionColors = {
    'New': 'bg-foreground text-background',
    'NOS': 'bg-brand-yellow text-foreground',
    'Used – Normal Use': 'bg-muted text-foreground',
    'Used – Fair': 'bg-brand-red text-background',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-16 md:pb-8">
        <div className="container px-4 py-8 md:py-12 max-w-6xl mx-auto">
          <Skeleton className="h-6 w-48 mb-6" />
          <div className="grid md:grid-cols-2 gap-8">
            <Skeleton className="aspect-square w-full" />
            <div className="space-y-4">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-32" />
              <Separator />
              <Skeleton className="h-32 w-full" />
              <Separator />
              <Skeleton className="h-24 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-16 md:pb-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-2">Product not found</h2>
          <Button onClick={() => navigate('/shop')} variant="outline">
            Back to Shop
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16 md:pb-8">
      <div className="container px-4 py-6 md:py-8 max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/" className="text-muted-foreground hover:text-foreground">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator><ChevronRight className="h-4 w-4" /></BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/shop" className="text-muted-foreground hover:text-foreground">Shop</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator><ChevronRight className="h-4 w-4" /></BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage className="text-foreground font-medium line-clamp-1">
                {product.title}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Image Section */}
          <div className="space-y-4">
            <Card className="overflow-hidden border-border">
              <div className="relative bg-muted/30 aspect-square">
                {product.images?.[0] ? (
                  <img
                    src={product.images[0]}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    No image available
                  </div>
                )}
                <Badge className={`absolute top-4 left-4 ${conditionColors[product.condition] || 'bg-muted'}`}>
                  {product.condition}
                </Badge>
              </div>
            </Card>
            {product.images?.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {product.images.slice(1, 5).map((img, i) => (
                  <Card key={i} className="overflow-hidden border-border cursor-pointer hover:border-foreground transition-colors">
                    <img src={img} alt="" className="w-full h-full object-cover aspect-square" />
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Details Section */}
          <div className="space-y-6">
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                {product.partType} / {product.partCategory}
              </p>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                {product.title}
              </h1>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl font-bold text-foreground">
                  {formatPrice(product.price)}
                </span>
                {product.compareAtPrice && (
                  <span className="text-lg text-muted-foreground line-through">
                    {formatPrice(product.compareAtPrice)}
                  </span>
                )}
              </div>
              {product.stock !== undefined && (
                <p className={`text-sm font-medium ${product.stock > 0 ? 'text-foreground' : 'text-brand-red'}`}>
                  {product.stock > 0 ? `In stock (${product.stock} available)` : 'Out of stock'}
                </p>
              )}
            </div>

            <Separator />

            {/* Description */}
            {product.description && (
              <div>
                <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Description
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {product.description}
                </p>
              </div>
            )}

            {/* Condition Notes */}
            {product.conditionNotes && (
              <div>
                <h3 className="font-semibold text-foreground mb-2">Condition Notes</h3>
                <p className="text-sm text-muted-foreground italic leading-relaxed">
                  {product.conditionNotes}
                </p>
              </div>
            )}

            {/* Fitment */}
            {product.fitment?.length > 0 && (
              <div>
                <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Fits
                </h3>
                <div className="space-y-1">
                  {product.fitment.map((f, i) => (
                    <p key={i} className="text-sm text-muted-foreground">
                      {f.yearFrom}–{f.yearTo} {f.make} {f.model}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Sizes (Apparel only) */}
            {product.sizes?.length > 0 && (
              <div>
                <h3 className="font-semibold text-foreground mb-2">Available Sizes</h3>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map(s => (
                    <Button
                      key={s}
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedSize(s)}
                      className={selectedSize === s ? 'border-foreground bg-foreground text-background' : ''}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Colors (Apparel only) */}
            {product.colors?.length > 0 && (
              <div>
                <h3 className="font-semibold text-foreground mb-2">Available Colors</h3>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map(c => (
                    <Button
                      key={c}
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedColor(c)}
                      className={selectedColor === c ? 'border-foreground bg-foreground text-background' : ''}
                    >
                      {c}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {product.tags?.length > 0 && (
              <div>
                <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-1">
                  {product.tags.map(t => (
                    <Badge key={t} variant="secondary" className="text-xs">
                      #{t}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            {/* Actions */}
            <div className="flex gap-3">
              <Button size="lg" className="flex-1 bg-foreground text-background hover:bg-muted">
                Order Now
              </Button>
              <Button size="lg" variant="outline" className="flex-1">
                View Details
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}